import { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { BillingInfoFieldName, TransactionCategory } from '@shipstation/financial-services-ui-components';
import type { BillingInfoData } from '@shipstation/financial-services-ui-components';
import { Button, Checkbox, Flex, Text } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard } from '@fortawesome/pro-regular-svg-icons/faCreditCard';
import { faLock } from '@fortawesome/pro-regular-svg-icons/faLock';
import { ModalContent } from '@shipstation/client-common/components/modal-content';
import { ModalFooter } from '@shipstation/client-common/components/modal-footer';

import { NOTIFICATION_TYPE_ERROR, NotificationService } from 'ui-components/services/notification-service';

import FsuiContextProvider from '../../../financial-services-ui/fsui-context-provider';
import { FinancialServicesUIProviderWrapper } from '../../../financial-services-ui/financial-services-ui-provider-wrapper';
import { updatePaymentMethod } from '../../../financial-services-ui/api';
import { getBillingInfoFromPaymentMethod } from '../../../financial-services-ui/mapping';
import type { PaymentMethodDetails, UpdatePaymentMethodBody } from '../../../financial-services-ui/types/fsui';
import { usePaymentAndSubscriptionMegaContext } from '../../../pages/settings/payment-and-subscription/context';
import { UserPermissionService } from '../../../services/user-permission-service';
import { selectPaymentMethodsHasActiveShipmentInvoicePayment } from '../../../store/payment-methods/payment-methods.selectors';
import Global from '../../../store/global-selectors';
import { noop } from 'ui-components/default-props/noop';
import { US } from '../../../constants/country-codes';
import {
  AuctanePayAccountSelect,
  NEW_PAYMENT_METHOD_VALUE,
} from '../../../components/wwex-registration-modal/components/auctane-pay-account-select';
import { PaymentMethodSection } from '../../../components/wwex-registration-modal/components/payment-method-section';
import { BillingAddressSummary } from '../../../components/wwex-registration-modal/components/billing-address-summary';
import { BillingAddressForm } from '../../../components/wwex-registration-modal/components/billing-address-form';
import type { Address } from '../../../components/wwex-registration-modal/types';
import { buildInteractionPayload, recordUpsInvoicedInteraction } from './record-ups-invoiced-interaction';
import { messages } from './messages';
import { useOnce } from '../../../hooks/use-once';
import formatters from '../../../intl/formatters';
import { AlertBanner } from '@shipstation/client-common/components/alert-banner';

type Props = {
  onClose: () => void;
  onAccept: () => void;
};

type BillingFormData = {
  fullName: string;
  companyName: string;
  address: Address;
};

const emptyAddress = (countryCode: string): Address => ({
  line1: '',
  line2: '',
  line3: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode,
});

const buildBillingDataFromBillingInfo = (billingInfo: BillingInfoData, sellerCountry: string): BillingFormData => ({
  fullName: billingInfo[BillingInfoFieldName.FullName] || '',
  companyName: billingInfo[BillingInfoFieldName.Company] || '',
  address: {
    line1: billingInfo[BillingInfoFieldName.Line1] || '',
    line2: billingInfo[BillingInfoFieldName.Line2] || '',
    line3: '',
    city: billingInfo[BillingInfoFieldName.City] || '',
    state: billingInfo[BillingInfoFieldName.State] || '',
    postalCode: billingInfo[BillingInfoFieldName.PostalCode] || '',
    countryCode: sellerCountry,
  },
});

const findPreferredPaymentMethodId = (methods: PaymentMethodDetails[]): string => {
  const freightMethod = methods.find((m) => m.assignedPreferences?.includes(TransactionCategory.Freight));
  if (freightMethod) return freightMethod.id;
  return methods.at(0)?.id ?? NEW_PAYMENT_METHOD_VALUE;
};

const UpsInvoicedNotificationModalContent = ({ onClose, onAccept }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const sellerCountry = useSelector(Global.selectSellerHomeCountry) ?? US;
  const userId = useSelector(Global.selectUserCurrentUserId);
  const hasFreightPaymentMethod = useSelector(selectPaymentMethodsHasActiveShipmentInvoicePayment);
  const hadFreightPaymentMethodAtStartRef = useRef(hasFreightPaymentMethod);
  const { paymentMethodDetails, reloadData } = usePaymentAndSubscriptionMegaContext();

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(() =>
    paymentMethodDetails.length > 0 ? findPreferredPaymentMethodId(paymentMethodDetails) : NEW_PAYMENT_METHOD_VALUE,
  );
  const [billingFormData, setBillingFormData] = useState<BillingFormData>(() => ({
    fullName: '',
    companyName: '',
    address: emptyAddress(sellerCountry),
  }));
  const [isEditingBillingAddress, setIsEditingBillingAddress] = useState(false);
  const [autoPayAccepted, setAutoPayAccepted] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const submitPaymentRef = useRef<(() => void) | null>(null);
  const isBusy = isSubmitting || isProcessingPayment;

  const isAddNewMode = selectedPaymentMethodId === NEW_PAYMENT_METHOD_VALUE;
  const selectedMethod = useMemo(
    () => paymentMethodDetails.find((m) => m.id === selectedPaymentMethodId),
    [paymentMethodDetails, selectedPaymentMethodId],
  );

  const seedBillingFromMethod = useCallback(
    (method: PaymentMethodDetails) => {
      const extracted = getBillingInfoFromPaymentMethod(method, {
        name: '',
        homeCountry: sellerCountry,
        phone: '',
        companyName: '',
      });
      if (extracted) {
        setBillingFormData(buildBillingDataFromBillingInfo(extracted, sellerCountry));
      }
    },
    [sellerCountry],
  );

  // Seed billing address from the initially selected payment method
  useOnce(() => {
    if (selectedMethod) {
      seedBillingFromMethod(selectedMethod);
    }
  });

  const handlePaymentMethodChange = useCallback(
    (id: string) => {
      setSelectedPaymentMethodId(id);
      setIsEditingBillingAddress(false);
      if (id === NEW_PAYMENT_METHOD_VALUE) return;
      const method = paymentMethodDetails.find((m) => m.id === id);
      if (method) seedBillingFromMethod(method);
    },
    [paymentMethodDetails, seedBillingFromMethod],
  );

  const handleSubmitFunctionReady = useCallback((fn: (() => void) | null) => {
    submitPaymentRef.current = fn;
  }, []);

  const handleBillingFormChange = useCallback((field: string, value: string | Address) => {
    setBillingFormData((prev) => (field === 'address' ? { ...prev, address: value as Address } : { ...prev, [field]: value as string }));
  }, []);

  const handleBillingFormBlur = useCallback(noop, []);

  const handleEditBillingAddress = useCallback(() => setIsEditingBillingAddress(true), []);
  const handleSaveBillingAddress = useCallback(() => setIsEditingBillingAddress(false), []);

  const handleAutoPayChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setAutoPayAccepted(event.currentTarget.checked);
  }, []);

  const ensureFreightPreference = useCallback(
    async (method: PaymentMethodDetails) => {
      if (method.assignedPreferences?.includes(TransactionCategory.Freight)) return;

      const payload: UpdatePaymentMethodBody = {
        paymentMethodId: method.id,
        assignedPreferences: [TransactionCategory.Freight], // update is additive
      };
      const result = await updatePaymentMethod(payload);
      if (!result.success) {
        throw new Error(result.errorMessage ?? 'Failed to assign Freight preference');
      }
      await reloadData?.();
    },
    [reloadData],
  );

  const showActivateError = useCallback(() => {
    NotificationService.showNotification({
      content: intl.formatMessage(messages.activateError),
      notificationType: NOTIFICATION_TYPE_ERROR,
    });
  }, [intl]);

  const finalizeActivation = useCallback(
    async (method?: PaymentMethodDetails) => {
      setIsSubmitting(true);
      try {
        if (method) await ensureFreightPreference(method);
        await recordUpsInvoicedInteraction(buildInteractionPayload(userId), dispatch);
        onAccept();
      } catch {
        showActivateError();
      } finally {
        setIsSubmitting(false);
        setIsProcessingPayment(false);
      }
    },
    [ensureFreightPreference, userId, dispatch, onAccept, showActivateError],
  );

  const handlePaymentMethodCreated = useCallback(
    (paymentMethodId: string, _billingInfo?: BillingInfoData, paymentMethod?: PaymentMethodDetails) => {
      setSelectedPaymentMethodId(paymentMethodId);
      void finalizeActivation(paymentMethod);
    },
    [finalizeActivation],
  );

  // Covers Adyen submission and poll/create failures inside PaymentMethodSection -- finalizeActivation's
  // own catch only covers failures after a method has already been created.
  const handlePaymentPipelineError = useCallback(() => {
    setIsProcessingPayment(false);
    setIsSubmitting(false);
    showActivateError();
  }, [showActivateError]);

  const handleActivate = useCallback(() => {
    if (isAddNewMode) {
      submitPaymentRef.current?.();
      return;
    }
    void finalizeActivation(selectedMethod);
  }, [isAddNewMode, selectedMethod, finalizeActivation]);

  const canActivate = (!isAddNewMode && !!selectedPaymentMethodId && autoPayAccepted) || isAddNewMode;

  const showBillingAddress = !isAddNewMode && !!selectedMethod;
  const saveBillingLabel = intl.formatMessage(messages.saveBillingAddress);

  return (
    <>
      <ModalContent>
        <Flex direction='column' gap='md'>
          <Text>
            <FormattedMessage {...messages.subtitle} values={{ b: formatters.b }} />
          </Text>

          <AlertBanner
            description={<FormattedMessage {...messages.infoBanner} />}
            icon={<FontAwesomeIcon icon={faCreditCard} />}
            status='inform'
          />

          <AuctanePayAccountSelect disabled={isBusy} onChange={handlePaymentMethodChange} value={selectedPaymentMethodId} />

          {isAddNewMode && (
            <PaymentMethodSection
              onCreationError={handlePaymentPipelineError}
              onPaymentMethodCreated={handlePaymentMethodCreated}
              onProcessingChange={setIsProcessingPayment}
              onSubmitFunctionReady={handleSubmitFunctionReady}
            />
          )}

          {hadFreightPaymentMethodAtStartRef.current && (
            <Text c='dimmed' fs='italic' size='sm'>
              <FormattedMessage {...messages.paymentMethodHelperText} />
            </Text>
          )}

          {showBillingAddress && !isEditingBillingAddress && (
            <BillingAddressSummary
              billingAddress={billingFormData.address}
              billingFullName={billingFormData.fullName}
              isDisabled={isBusy}
              onEdit={handleEditBillingAddress}
            />
          )}

          {showBillingAddress && isEditingBillingAddress && (
            <>
              <BillingAddressForm
                data={billingFormData}
                isDisabled={isBusy}
                onBlur={handleBillingFormBlur}
                onChange={handleBillingFormChange}
              />
              <Button aria-label={saveBillingLabel} onClick={handleSaveBillingAddress} size='sm' variant='secondary'>
                {saveBillingLabel}
              </Button>
            </>
          )}

          <Checkbox
            checked={autoPayAccepted}
            description={<FormattedMessage {...messages.autoPayDescription} />}
            disabled={isBusy}
            label={<FormattedMessage {...messages.autoPayLabel} />}
            onChange={handleAutoPayChange}
          />
        </Flex>
      </ModalContent>

      <ModalFooter>
        <Button disabled={isBusy} onClick={onClose} variant='ghost'>
          <FormattedMessage {...messages.cancel} />
        </Button>
        <Button disabled={isBusy || !canActivate} loading={isBusy} onClick={handleActivate} variant='primary'>
          <FormattedMessage {...messages.activate} />
        </Button>
      </ModalFooter>
    </>
  );
};

const UserLacksPermissionBody = ({ onClose }: Pick<Props, 'onClose'>) => {
  return (
    <>
      <ModalContent>
        <Flex direction='column' gap='md'>
          <Text>
            <FormattedMessage {...messages.subtitle} values={{ b: formatters.b }} />
          </Text>

          <AlertBanner
            description={<FormattedMessage {...messages.infoBanner} />}
            icon={<FontAwesomeIcon icon={faCreditCard} />}
            status='inform'
          />

          <AlertBanner
            description={<FormattedMessage {...messages.readOnlyNote} />}
            icon={<FontAwesomeIcon icon={faLock} />}
            status='neutral'
          />
        </Flex>
      </ModalContent>
      <ModalFooter>
        <Button onClick={onClose} variant='ghost'>
          <FormattedMessage {...messages.cancel} />
        </Button>
      </ModalFooter>
    </>
  );
};

export const UpsInvoicedNotificationModal = ({ onClose, onAccept }: Props) => {
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency) ?? 'USD';
  const fsuiBillingInfoRef = useRef<BillingInfoData | undefined>(undefined);

  const userRoles = useSelector(Global.selectUseruserRoles);
  const hasBillingPermission = UserPermissionService.userHasAccountManagementRole(userRoles);

  const handleFsuiBillingInfoUpdate = useCallback((billingInfo?: BillingInfoData) => {
    fsuiBillingInfoRef.current = billingInfo;
  }, []);

  // return early so we don't need to create a session
  if (!hasBillingPermission) {
    return <UserLacksPermissionBody onClose={onClose} />;
  }

  return (
    <FsuiContextProvider currencyCode={sellerCurrencyCode} transactionCategory={TransactionCategory.Freight}>
      <FinancialServicesUIProviderWrapper onBillingInfoUpdate={handleFsuiBillingInfoUpdate}>
        <UpsInvoicedNotificationModalContent onAccept={onAccept} onClose={onClose} />
      </FinancialServicesUIProviderWrapper>
    </FsuiContextProvider>
  );
};
